import type { ProgramConfig } from '../engine/protocol';
import type { ProgramEntry } from './schema';

type Override = (cfg: ProgramConfig, entry: ProgramEntry) => ProgramConfig;

const setSlot =
  (slot: number, params: Record<string, number>): Override =>
  (cfg) => {
    const s = cfg.slots[slot];
    if (s) Object.assign(s.params, params);
    return cfg;
  };

const chain =
  (...fns: Override[]): Override =>
  (cfg, entry) =>
    fns.reduce((c, f) => f(c, entry), cfg);

/**
 * Hand-tuned corrections per program id, applied after heuristic mapping.
 * Tune these by playing the program next to the hardware (or by ear) and
 * add more as favorites emerge — this is the designed workflow, not a hack.
 */
export const PROGRAM_OVERRIDES: Record<string, Override> = {
  // FLt.5 "60dB LPF" — five cascaded filter slots; make them all track X.
  'FLt.5': (cfg) => {
    cfg.routes = cfg.routes.filter((r) => !(r.source === 'x1' && r.param === 'cutoff'));
    for (let i = 0; i < 5; i++) {
      cfg.routes.push({ source: 'x1', slot: i, param: 'cutoff', min: 0, max: 1, curve: 'lin' });
      cfg.routes.push({ source: 'y1', slot: i, param: 'reso', min: 0, max: 0.55, curve: 'lin' });
    }
    return cfg;
  },
  // FLt.6 "BPF+" — bandpass with attitude.
  'FLt.6': setSlot(0, { mode: 0.5, reso: 0.6, drive: 0.35 }),
  // FLt.7 "Vowel Filter" — two BP filters need distinct rest formants.
  'FLt.7': chain(setSlot(0, { mode: 0.5, reso: 0.8, cutoff: 0.35 }), setSlot(1, { mode: 0.5, reso: 0.8, cutoff: 0.6 })),
  // dLY.2 "Ping Pong Delay"
  'dLY.2': setSlot(0, { pingpong: 1 }),
  // dLY.4 "Reverse Delay" — looper slot runs reversed as the "reverse" feel.
  'dLY.4': setSlot(0, { on: 1, direction: 0, beat: 0.55 }),
  // dL.18 "KP2 Special Delay" — two tape echoes, offset times, both on Y depth.
  'dL.18': chain(
    setSlot(0, { time: 0.4 }),
    setSlot(1, { time: 0.62 }),
    (cfg) => {
      cfg.routes.push({ source: 'y1', slot: 1, param: 'depth', min: 0.15, max: 1, curve: 'lin' });
      return cfg;
    }
  ),
  // rEv.4 "Gated Reverb" — X is gate time; make the gate audible by default.
  'rEv.4': setSlot(0, { gate: 0.5, time: 0.6, depth: 0.7 }),
  // Mod.9 "Vinyl Break" — the pad IS the brake: engage on touch.
  'Mod.9': (cfg) => {
    const s = cfg.slots[0];
    if (s) s.params['engage'] = 1;
    return cfg;
  },
  // Mo.10 "Break Reverb" — brake engaged while touched, echoes into reverb.
  'Mo.10': chain(setSlot(0, { engage: 1 }), setSlot(1, { depth: 0.4 }), setSlot(2, { depth: 0.45 })),
  // LFo.7 "Chorus 2 Delay" — chorus always wet inside the unit.
  'LFo.7': setSlot(0, { mix: 0.8 }),
  // LF.11 "Slicer 2 Break" — slicer preset shape.
  'LF.11': setSlot(0, { mode: 0.5, shape: 0.5, depth: 1 }),
  // LoP.1/LoP.2 — second looper engages via touch2; keep slice lengths apart.
  'LoP.1': chain(setSlot(0, { on: 1 }), setSlot(1, { on: 0 })),
  'LoP.2': chain(setSlot(0, { on: 1 }), setSlot(1, { on: 0 })),
  // Lo.20 "Pitch Looper" — Y sweeps ±1 octave.
  'Lo.20': (cfg) => {
    cfg.routes = cfg.routes.filter((r) => !(r.source === 'y1'));
    cfg.routes.push({ source: 'y1', slot: 1, param: 'pitch', min: 0.25, max: 0.75, curve: 'lin' });
    return cfg;
  },
  // KAS.6 "Fun Fun Looper" — speed axis spans reverse↔forward.
  'KAS.6': (cfg) => {
    cfg.routes.push({ source: 'y1', slot: 0, param: 'direction', min: 0, max: 1, curve: 'switch' });
    return cfg;
  },
  // SYn.1 "Analog Siren 2 Sweep" — droning osc, touch-triggered envelope off.
  'SYn.1': setSlot(0, { trigger: 0, level: 0.75, decay: 1 }),
  // drM.3 "Dope Kick" — percussive trigger on every touch; Y2 approximates
  // "Decimator LFO Speed" as a rate sweep.
  'drM.3': chain(setSlot(0, { trigger: 1, decay: 0.35, pitch: 0.12, level: 0.9 }), (cfg) => {
    cfg.routes.push({ source: 'y2', slot: 3, param: 'rate', min: 0.8, max: 0.15, curve: 'lin' });
    return cfg;
  }),
  // drM.1 "Phat Kick & Tom & Snare"
  'drM.1': setSlot(0, { trigger: 1, decay: 0.4, level: 0.9 }),
  // drM.4 "Snare Roll 2 Burst" — sample osc in snare mode, retriggered.
  'drM.4': setSlot(0, { sample: 0.66, trigger: 1, decay: 0.25, level: 0.85 }),

  // --- Shimmer voicings (rEv family) ---------------------------------------
  // rEv.1 "Reverb 2 Cathedral" — classic +12 bloom.
  'rEv.1': setSlot(0, { shift: 0.75, feedback: 0.55, damp: 0.35 }),
  // rEv.5 "Submarine Reverb" — dark, pitched-down wash.
  'rEv.5': setSlot(0, { shift: 0.25, feedback: 0.5, damp: 0.75 }),
  // rEv.6 "Riser Reverb" — +12 regenerating build.
  'rEv.6': setSlot(0, { shift: 0.75, feedback: 0.82, damp: 0.3 }),

  // --- Vocal programs -------------------------------------------------------
  // voC.2 "Hardtune C - G" — X sweeps the scale root C..G.
  'voC.2': (cfg) => {
    cfg.routes = cfg.routes.map((r) =>
      r.source === 'x1' && r.param === 'root' ? { ...r, min: 0, max: 7 / 11 } : r
    );
    const s = cfg.slots[0];
    if (s) s.params['amount'] = 1;
    return cfg;
  },
  // voC.3 "Hardtune G - D" — root G upward.
  'voC.3': (cfg) => {
    cfg.routes = cfg.routes.map((r) =>
      r.source === 'x1' && r.param === 'root' ? { ...r, min: 7 / 11, max: 1 } : r
    );
    return cfg;
  },
  // voC.5 "Robot Speech" — hard snap + formant play.
  'voC.5': setSlot(0, { amount: 1, harmony: 0.5 }),

  // --- Drone / resonator ----------------------------------------------------
  // Mo.15 "Fingering F Penta" — strings parked on an F-rooted set.
  'Mo.15': setSlot(0, { pitch: 0.36, decay: 0.75, sens: 0.7 }),
  // SYn.9 "Basic Noise 2 Chord" — resonator sits mostly wet when engaged.
  'SYn.9': setSlot(1, { decay: 0.65, tone: 0.6 }),

  // --- Kaoss quad-morph approximations ---------------------------------------
  // KAS.2 "Kaoss Mix 1" [Decimator, Pitch Shifter, Distortion, Filter]:
  // hardware morphs FX by pad quadrant; we spread them across both axes.
  'KAS.2': (cfg) => {
    cfg.routes = [
      { source: 'x1', slot: 0, param: 'rate', min: 1, max: 0.1, curve: 'lin' },
      { source: 'x1', slot: 2, param: 'drive', min: 0.1, max: 0.65, curve: 'lin' },
      { source: 'y1', slot: 1, param: 'pitch', min: 0.3, max: 0.7, curve: 'lin' },
      { source: 'y1', slot: 3, param: 'cutoff', min: 0.15, max: 1, curve: 'lin' },
    ];
    return cfg;
  },
  // KAS.3 "Kaoss Mix 2" [Ring Mod, Flanger, Distortion, Grain Shifter]
  'KAS.3': (cfg) => {
    cfg.routes = [
      { source: 'x1', slot: 0, param: 'freq', min: 0.1, max: 0.9, curve: 'lin' },
      { source: 'x1', slot: 2, param: 'drive', min: 0.1, max: 0.6, curve: 'lin' },
      { source: 'y1', slot: 1, param: 'depth', min: 0, max: 0.8, curve: 'lin' },
      { source: 'y1', slot: 3, param: 'mix', min: 0, max: 1, curve: 'lin' },
    ];
    return cfg;
  },

  // Co.14/Co.15 — IR character selection per program.
  'Co.14': setSlot(0, { ir: 0, depth: 1 }), // Cheap Speaker
  'Co.15': setSlot(0, { ir: 0.33, depth: 1 }), // Amp Cabinet
  'voC.7': setSlot(0, { ir: 0.66, depth: 1 }), // Megaphone

  // KAS.1 "2x2 DJ Switches x2" — hardware splits the pad into 2x2 zones; we
  // approximate by spreading the four effects across the two axes.
  'KAS.1': (cfg) => {
    cfg.routes = cfg.routes.filter((r) => r.source === 'touch2');
    cfg.routes.push(
      { source: 'x1', slot: 0, param: 'beat', min: 0, max: 1, curve: 'lin' },
      { source: 'x1', slot: 1, param: 'cutoff', min: 0.15, max: 1, curve: 'lin' },
      { source: 'y1', slot: 0, param: 'direction', min: 0, max: 1, curve: 'switch' },
      { source: 'y1', slot: 1, param: 'reso', min: 0, max: 0.7, curve: 'lin' },
      { source: 'x2', slot: 2, param: 'breakTime', min: 0.1, max: 0.9, curve: 'lin' },
      { source: 'y2', slot: 3, param: 'speed', min: 0.2, max: 0.9, curve: 'lin' }
    );
    const vb = cfg.slots[2];
    if (vb) vb.params['engage'] = 0; // engaged via touch2 FX-change gesture
    return cfg;
  },
  // Mo.11 "Easy Scratch 2 Break" — X wiggles the platter directly.
  'Mo.11': (cfg) => {
    cfg.routes.push({ source: 'x1', slot: 0, param: 'scratch', min: 0.15, max: 0.85, curve: 'lin' });
    const s = cfg.slots[0];
    if (s) s.params['engage'] = 1;
    return cfg;
  },
  // voC.4 "Unison & Formant" — touch2 is the 2nd harmony; its X2/Y2 routes do
  // the work on our single vocal unit, so touch2 itself just keeps mix wet.
  'voC.4': (cfg) => {
    cfg.routes.push({ source: 'touch2', slot: 0, param: 'mix', min: 1, max: 1, curve: 'switch' });
    return cfg;
  },
};
